/**
 * NetworkStatTile — the rich, alive landing-page tile for one network (LANDING).
 *
 * Shows a compact row of vitals (rooms · agents · active · success · consumption
 * vs budget · outcome) over the tile face, an expand chevron for the inline room
 * list, and the ⋮ actions menu (reused from the existing Networks-tab handlers).
 * Data is one `networkVitals` object from `GET /networks/dashboard`; absent
 * optional fields (budget, outcome) simply don't render.
 */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Boxes, Users, Activity, Server } from 'lucide-react';
import {
  StatChip, SuccessRing, ConsumptionMeter, ActivePulse, OutcomeChip,
} from '@stepflow/ui-shared/components/dashboard/vitalWidgets.jsx';
import NetworkAvatar, { networkVisual } from '@stepflow/ui-shared/components/dashboard/NetworkAvatar.jsx';

const STATUS_DOT = {
  RELEASED: 'bg-emerald-500',
  ACTIVE: 'bg-blue-500',
  DRAFT: 'bg-gray-300',
  DELETED: 'bg-red-400',
};

export default function NetworkStatTile({
  vitals, expanded, onToggleExpand, onOpen, actionsMenu, justLive, tone,
}) {
  const v = vitals || {};
  const dot = STATUS_DOT[String(v.status || '').toUpperCase()] || 'bg-gray-300';
  const rooms = Number(v.roomCount || 0);
  const active = Number(v.activeRoomCount || 0);
  // Consumption unit toggle — per tile. Default to $ (the ask), EXCEPT when a
  // points budget is allocated: then default to points so the budget bar (a
  // points-only concept — there's no $ budget) stays visible. Either way the user
  // can flip via the dropdown. Local state so each tile is independent.
  const hasBudget = Number(v.budgetPoints || 0) > 0;
  const [costUnit, setCostUnit] = useState(hasBudget ? 'points' : 'usd');
  // Border tone. Only ONE functional alert remains: config "needs credential"
  // (amber). The green "config-ok / live-run" border was REMOVED — it collided
  // with the network-hue identity border below, and live state is already shown
  // via the "live" badge + the active pulse. Everything that isn't a pending-
  // config alert carries the NETWORK's OWN hue so the whole tile reads as that
  // network's identity.
  // Seed the identity hue from the portable visualSeed when present (registry
  // networks), else the networkId — so the border matches the avatar's color.
  const netHue = networkVisual(v.visualSeed || v.networkId).hue;
  const statusTone = tone === 'yellow'
    ? 'border-amber-300 bg-amber-50/40'
    : null;  // null → fall through to the network-hue identity border below
  // Identity border: a soft tint of the network's hue (low S, high L) so it's
  // present but not loud. Only when no functional status tone applies. Inline
  // style (a dynamic hue can't be a static Tailwind class); a JS hover deepens
  // it since an inline borderColor would override a Tailwind hover: class.
  const [hovered, setHovered] = useState(false);
  const identityStyle = statusTone ? undefined : {
    borderColor: `hsl(${netHue} 55% ${hovered ? 68 : 82}%)`,
  };

  return (
    <motion.div
      layout
      style={identityStyle}
      onMouseEnter={() => !statusTone && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`relative rounded-xl border bg-white p-3 transition-colors ${
        statusTone || ''
      }`}
      data-testid={`network-stat-tile-${v.networkId}`}
    >
      {/* Header: status dot + two-deck name/id (click → open workbench) + actions.
          Upper deck = display name (can collide across networks); lower deck =
          the distinct networkId (pet/registry) so operators can tell apart two
          networks that happen to share a name. */}
      <div className="flex items-start justify-between gap-2">
        <button
          type="button"
          onClick={() => onOpen?.(v)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          data-testid={`network-stat-open-${v.networkId}`}
        >
          {/* Permanent per-network visual (icon + hash gradient) with a small
              status dot overlaid so lifecycle state is still glanceable. */}
          <span className="relative shrink-0">
            <NetworkAvatar
              networkId={v.networkId}
              name={v.name}
              size={30}
              seed={v.visualSeed || null}
              shape={v.fromRegistry ? 'hexagon' : 'square'}
            />
            <span
              className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white ${dot}`}
              aria-hidden
            />
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-2">
              <span className="min-w-0 truncate text-sm font-semibold text-gray-900">
                {v.name || v.networkId}
              </span>
              {justLive ? (
                <span className="shrink-0 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-emerald-600">
                  live
                </span>
              ) : null}
            </span>
            {v.networkId ? (
              <span className="mt-0.5 flex items-center gap-1.5 leading-tight">
                <span
                  className="min-w-0 truncate font-mono text-[10px] text-gray-400"
                  title={v.networkId}
                  data-testid={`network-stat-id-${v.networkId}`}
                >
                  {v.networkId}
                </span>
                {v.stepflowVersion ? (
                  <span
                    className="shrink-0 rounded bg-gray-100 px-1 py-px font-mono text-[9px] text-gray-500"
                    title={`Released version ${v.stepflowVersion}`}
                    data-testid={`network-stat-version-${v.networkId}`}
                  >
                    v{v.stepflowVersion}
                  </span>
                ) : null}
              </span>
            ) : null}
          </span>
        </button>
        <div className="flex shrink-0 items-center gap-1">
          <OutcomeChip pct={v.outcomePct} />
          {actionsMenu}
        </div>
      </div>

      {/* Vitals row 1: rooms · agents · active. */}
      <div className="mt-2.5 flex items-center gap-3">
        <StatChip icon={Boxes} value={rooms} label="rooms" tone="blue"
          title={`${rooms} room${rooms === 1 ? '' : 's'}`} />
        <StatChip icon={Users} value={v.totalAgents ?? (Number(v.agentCount || 0) * rooms)} label="agents"
          tone="indigo" title={`${v.agentCount || 0} agents × ${rooms} rooms`} />
        <div className="ml-auto flex items-center gap-1" title="Live rooms">
          <Activity className="h-3.5 w-3.5 text-emerald-600" aria-hidden />
          <ActivePulse count={active} />
        </div>
      </div>

      {/* Vitals row 2: success ring + consumption/budget. */}
      <div className="mt-2 flex items-center gap-3">
        <div className="flex items-center gap-1.5" title="Success rate (lifetime)">
          <SuccessRing pct={v.successRatePct} />
          <span className="text-[10px] text-gray-500">
            {v.runsTotal ? `${v.runsSucceeded}/${v.runsTotal}` : 'no runs'}
          </span>
        </div>
        <div className="ml-auto flex min-w-0 items-center gap-1"
             title={costUnit === 'usd' ? 'Estimated cost consumed' : 'Points consumed'}>
          <Server className="h-3.5 w-3.5 shrink-0 text-gray-500" aria-hidden />
          <ConsumptionMeter
            unit={costUnit}
            consumed={v.consumedPoints}
            consumedUsd={v.consumedUsd}
            budget={v.budgetPoints}
            hideUnit
          />
          {/* Per-tile unit toggle — default $, switch to points. Tiny, unobtrusive. */}
          <select
            value={costUnit}
            onChange={(e) => setCostUnit(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            aria-label="Consumption unit"
            title="Show cost in $ or points"
            data-testid={`network-stat-unit-${v.networkId}`}
            className="shrink-0 cursor-pointer rounded border-0 bg-transparent py-0 pl-0.5 pr-3 text-[10px] text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-0"
          >
            <option value="usd">$</option>
            <option value="points">pts</option>
          </select>
        </div>
      </div>

      {/* Expand toggle for the inline room list. */}
      {rooms > 0 ? (
        <button
          type="button"
          onClick={() => onToggleExpand?.(v)}
          className="mt-2 flex w-full items-center justify-center gap-1 rounded-md border border-gray-100 py-1 text-[11px] font-medium text-gray-500 hover:bg-gray-50"
          data-testid={`network-stat-expand-${v.networkId}`}
          aria-expanded={!!expanded}
        >
          {expanded ? 'Hide rooms' : `View ${rooms} room${rooms === 1 ? '' : 's'}`}
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} aria-hidden />
        </button>
      ) : null}
    </motion.div>
  );
}
